import React, {useCallback, useEffect, useState} from 'react';
import {NewsletterProps} from '../../../types';
import {useCabinedMode, useIndustryList, validateEmail} from 'epic-ue-shared';
import {useMarketoNewsletterSubscribe, useNewsletterAccount} from 'epic-ue-newsletter';
import {useLocalization} from '@epic-mw/localization';

const getIndustryValue = (value: string): string => (!value ? '' : value);

export const withNewsletterLogic = <T extends NewsletterProps>(
    Component: (props: T) => JSX.Element
) =>
    function NewsletterLogic(props: T): JSX.Element | null {
        const getMessage = useLocalization();
        const {
            requiredFieldMsg = getMessage('epic.enterprise.contact.us.required.field.text') || '',
            invalidEmailMsg = getMessage('epic.enterprise.contact.us.invalid.email.text') || '',
            subscribeErrorMsg = getMessage('epic.ue.signup.newsletter.subscribe_failed') || '',
            submitButtonName = getMessage('epic.enterprise.engagement.newsletter.button.text') ||
                '',
            emailInputPlaceholder = getMessage('epic.enterprise.contact.us.email.text') || '',
            privacyPolicyDescription = getMessage('epic.newsletter.globaloptin') || '',
            marketoLeadParams,
            successTitle = getMessage('epic.ue.signup.newsletter.success') || '',
            successMessage = ''
        } = props;
        const {account} = useNewsletterAccount();
        const [email, setEmail] = useState(account?.email || '');
        const [selectedIndustry, setSelectedIndustry] = useState('');
        const [isEmailReadOnly, setIsEmailReadOnly] = useState(false);
        const [emailPristine, setEmailPristine] = useState(!email);
        const [emailValid, setEmailValid] = useState(true);
        const [industryValid, setIndustryValid] = useState(true);
        const industryList = useIndustryList();
        const staticIndustry = props.staticIndustry;
        const {subscribed, subscribe, loading, error} = useMarketoNewsletterSubscribe();
        const hasEmail = Boolean(account && account.isLoggedIn && account.email);
        const cabinedMode = useCabinedMode();

        useEffect(() => {
            if (account && account.email && !email && emailPristine) {
                setEmail(account.email);
            }
        }, [account, setEmail, email, emailPristine]);

        if (hasEmail && hasEmail !== isEmailReadOnly) {
            setIsEmailReadOnly(hasEmail);
        }

        const handleBlur = useCallback(
            (name, value) => {
                switch (name) {
                    case 'email':
                        if (!emailPristine) {
                            setEmailValid(validateEmail(value));
                        }
                        break;
                    case 'industry':
                        setIndustryValid(!!getIndustryValue(value));
                        break;
                    default:
                        break;
                }
            },
            [emailPristine, setEmailValid, setIndustryValid]
        );

        const handleChange = useCallback(
            (name, val) => {
                const value = val.replace(/^\s+/g, '');
                const industry = getIndustryValue(value);
                switch (name) {
                    case 'email':
                        setEmail(value);
                        setEmailPristine(false);
                        if (!emailValid) {
                            setEmailValid(validateEmail(value));
                        }
                        break;
                    case 'industry':
                        setSelectedIndustry(industry);
                        if (!industryValid) {
                            setIndustryValid(!!industry);
                        }
                        break;
                    default:
                        break;
                }
            },
            [setEmail, setEmailPristine, setEmailValid, setIndustryValid, industryValid, emailValid]
        );

        const handleSubmit = useCallback(
            (email, industry) => {
                const industryVal = staticIndustry || getIndustryValue(industry);
                const isEmailValid = validateEmail(email) || !!account?.email;
                if (!isEmailReadOnly) {
                    setEmailValid(isEmailValid);
                }
                setIndustryValid(!!industryVal);
                if (isEmailValid && industryVal && !subscribed && !loading) {
                    subscribe(email || account?.email, industryVal, marketoLeadParams);
                }
            },
            [
                emailValid,
                emailPristine,
                marketoLeadParams,
                setEmailPristine,
                setIndustryValid,
                isEmailReadOnly,
                account,
                subscribed,
                loading,
                staticIndustry
            ]
        );

        useEffect(() => {
            if (error) {
                setEmailValid(false);
            }
        }, [setEmailValid, error]);

        if (cabinedMode) {
            return null;
        }

        let emailErrorMsg = '';
        let industryErrorMsg = '';
        let disableSubmitButton = false;

        if (!emailValid) {
            if (!email) {
                emailErrorMsg = requiredFieldMsg;
            } else {
                emailErrorMsg = invalidEmailMsg;
            }
        }

        if (!industryValid) {
            industryErrorMsg = requiredFieldMsg;
        }

        if (!industryValid || !emailValid) {
            disableSubmitButton = true;
        }

        if (error) {
            emailErrorMsg = subscribeErrorMsg;
        }

        return (
            <Component
                {...props}
                onChange={handleChange}
                onBlur={handleBlur}
                onSubmit={handleSubmit}
                emailInputValue={email}
                emailInputError={!emailValid}
                emailInputPlaceholder={emailInputPlaceholder}
                emailInputHelperText={emailErrorMsg}
                emailInputDisable={isEmailReadOnly}
                industryInputError={!industryValid}
                industryInputHelperText={industryErrorMsg}
                submitButtonName={submitButtonName}
                industrySelected={selectedIndustry}
                hideSubmitButton={subscribed}
                disableSubmitButton={disableSubmitButton}
                industries={industryList}
                disable={loading}
                privacyPolicyDescription={privacyPolicyDescription}
                successTitle={successTitle}
                successMessage={successMessage}
                subscribed={subscribed}
            />
        );
    };
